import { supabase } from "@/integrations/supabase/client";

// Types based on carbon_emissions.csv structure
export interface CarbonEmissionEntry {
  year: number;
  value: number; // CO2 emissions in million tonnes
}

export interface CarbonEmissionsData {
  id: number;
  region_id: string;
  emissions_yearly_data: CarbonEmissionEntry[];
  created_at?: string;
  updated_at?: string;
}

export interface ProcessedCarbonData {
  regionId: string;
  totalEmissions: number;
  latestYear: number;
  growthRate: number;
  peakYear: number;
  peakEmissions: number;
  averageEmissions: number;
  esgRating: string;
  trend: "increasing" | "decreasing" | "stable";
  historicalData: CarbonEmissionEntry[];
  projectedReduction: number;
  reductionTarget: number;
  lastUpdated: string;
}

// Cache to avoid repeated API calls
const emissionsCache = new Map<string, ProcessedCarbonData>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let lastCacheTime = 0;

/**
 * Fetch raw carbon emissions data for a specific region from Supabase
 */
export async function fetchCarbonEmissionsByRegion(
  regionId: string,
): Promise<CarbonEmissionsData | null> {
  try {
    console.log(
      `🌱 [CARBON SERVICE] Fetching emissions data for region: ${regionId}`,
    );

    const { data, error } = await supabase
      .from("carbon_emissions")
      .select("*")
      .eq("region_id", regionId.toLowerCase())
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        console.log(
          `🌱 [CARBON SERVICE] No emissions data found for region: ${regionId}`,
        );
        return null;
      }
      console.error("🌱 [CARBON SERVICE] Database error:", error);
      throw new Error(`Failed to fetch carbon emissions: ${error.message}`);
    }

    if (!data) {
      console.log(
        `🌱 [CARBON SERVICE] No data returned for region: ${regionId}`,
      );
      return null;
    }

    console.log(
      `🌱 [CARBON SERVICE] Successfully fetched data for ${regionId}:`,
      data,
    );
    return data as CarbonEmissionsData;
  } catch (error) {
    console.error(
      "🌱 [CARBON SERVICE] Error in fetchCarbonEmissionsByRegion:",
      error,
    );
    throw error;
  }
}

/**
 * Process raw carbon emissions data into analytics-ready format
 */
export function processCarbonData(
  rawData: CarbonEmissionsData,
): ProcessedCarbonData {
  const { region_id, emissions_yearly_data } = rawData;

  // Sort data by year
  const sortedData = [...emissions_yearly_data].sort((a, b) => a.year - b.year);
  const latestEntry = sortedData[sortedData.length - 1];
  const previousEntry = sortedData[sortedData.length - 2];

  // Calculate 5-year CAGR (Compound Annual Growth Rate) for a stable signal.
  // Falls back to 1-year YoY if fewer than 6 data points are available.
  let growthRate = 0;
  const cagrWindow = sortedData.slice(-6); // up to 6 points = 5 intervals
  if (cagrWindow.length >= 2) {
    const baseVal = cagrWindow[0].value;
    const endVal = cagrWindow[cagrWindow.length - 1].value;
    const n = cagrWindow.length - 1;
    if (baseVal > 0 && endVal > 0) {
      growthRate = (Math.pow(endVal / baseVal, 1 / n) - 1) * 100;
    }
  } else if (previousEntry && latestEntry && previousEntry.value > 0) {
    growthRate =
      ((latestEntry.value - previousEntry.value) / previousEntry.value) * 100;
  }

  // Find peak emissions
  const peakEntry = sortedData.reduce((max, current) =>
    current.value > max.value ? current : max,
  );

  // Calculate average emissions
  const averageEmissions =
    sortedData.reduce((sum, entry) => sum + entry.value, 0) / sortedData.length;

  // Determine trend over last 5 years using a normalised % per year rate,
  // so the threshold is scale-invariant across small and large emitters.
  const recentData = sortedData.slice(-5);
  let trendPctPerYear = 0;
  if (recentData.length > 1 && recentData[0].value > 0) {
    trendPctPerYear =
      ((recentData[recentData.length - 1].value - recentData[0].value) /
        recentData[0].value /
        (recentData.length - 1)) *
      100;
  }

  let trend: "increasing" | "decreasing" | "stable";
  if (Math.abs(trendPctPerYear) < 1) {
    // < 1 % per year → stable
    trend = "stable";
  } else if (trendPctPerYear > 0) {
    trend = "increasing";
  } else {
    trend = "decreasing";
  }

  // ESG rating: normalised relative to the region's own peak emissions.
  // This makes the scale invariant — a large country and a small city are
  // judged by how much they have reduced from their worst, not by raw tonnes.
  const pctBelowPeak =
    peakEntry.value > 0
      ? (peakEntry.value - latestEntry.value) / peakEntry.value
      : 0;

  let esgRating = "C";
  if (trend === "decreasing" && pctBelowPeak >= 0.3) {
    esgRating = "A+"; // ≥30 % below peak and still falling
  } else if (trend === "decreasing" && pctBelowPeak >= 0.15) {
    esgRating = "A"; // ≥15 % below peak and still falling
  } else if (pctBelowPeak >= 0.1) {
    esgRating = "B+"; // ≥10 % below peak (any trend)
  } else if (trend !== "increasing") {
    esgRating = "B"; // stable or improving but close to peak
  } else if (pctBelowPeak >= 0) {
    esgRating = "C+"; // still below peak but rising
  }
  // else 'C': emissions above historic peak

  // Paris-aligned indicative target: 43 % reduction from latest year by 2030
  // (IPCC AR6 WG3 median pathway for 1.5 °C). Labelled as an estimate because
  // actual NDC commitments vary by country.
  const projectedReduction = latestEntry.value * 0.43;
  const reductionTarget = latestEntry.value - projectedReduction;

  return {
    regionId: region_id,
    totalEmissions: Math.round(latestEntry.value * 100) / 100,
    latestYear: latestEntry.year,
    growthRate: Math.round(growthRate * 100) / 100,
    peakYear: peakEntry.year,
    peakEmissions: Math.round(peakEntry.value * 100) / 100,
    averageEmissions: Math.round(averageEmissions * 100) / 100,
    esgRating,
    trend,
    historicalData: sortedData,
    projectedReduction: Math.round(projectedReduction * 100) / 100,
    reductionTarget: Math.round(reductionTarget * 100) / 100,
    lastUpdated: rawData.updated_at || new Date().toISOString(),
  };
}

/**
 * Get processed carbon data for a region with caching
 */
export async function getRegionCarbonData(
  regionId: string,
): Promise<ProcessedCarbonData | null> {
  try {
    // Check cache first
    const now = Date.now();
    if (emissionsCache.has(regionId) && now - lastCacheTime < CACHE_DURATION) {
      console.log(`🌱 [CARBON SERVICE] Returning cached data for ${regionId}`);
      return emissionsCache.get(regionId) || null;
    }

    // Fetch fresh data
    const rawData = await fetchCarbonEmissionsByRegion(regionId);

    if (
      !rawData ||
      !rawData.emissions_yearly_data ||
      rawData.emissions_yearly_data.length === 0
    ) {
      console.log(
        `🌱 [CARBON SERVICE] No valid emissions data for region: ${regionId}`,
      );
      return null;
    }

    // Process and cache the data
    const processedData = processCarbonData(rawData);
    emissionsCache.set(regionId, processedData);
    lastCacheTime = now;

    console.log(
      `🌱 [CARBON SERVICE] Processed and cached data for ${regionId}:`,
      processedData,
    );
    return processedData;
  } catch (error) {
    console.error(
      `🌱 [CARBON SERVICE] Error getting carbon data for ${regionId}:`,
      error,
    );
    return null;
  }
}

/**
 * Get carbon emissions data for multiple regions (for comparison)
 */
export async function getMultipleRegionsCarbonData(
  regionIds: string[],
): Promise<ProcessedCarbonData[]> {
  try {
    const promises = regionIds.map((regionId) => getRegionCarbonData(regionId));
    const results = await Promise.allSettled(promises);

    return results
      .filter(
        (result): result is PromiseFulfilledResult<ProcessedCarbonData> =>
          result.status === "fulfilled" && result.value !== null,
      )
      .map((result) => result.value);
  } catch (error) {
    console.error(
      "🌱 [CARBON SERVICE] Error fetching multiple regions data:",
      error,
    );
    return [];
  }
}

/**
 * Get sector breakdown estimates (since not in CSV, we'll generate realistic estimates)
 */
export function generateSectorBreakdown(totalEmissions: number) {
  return [
    {
      sector: "Energy",
      emissions: Math.round(totalEmissions * 0.73 * 100) / 100,
      percentage: 73,
      color: "amber",
      icon: "Zap",
    },
    {
      sector: "Industry",
      emissions: Math.round(totalEmissions * 0.16 * 100) / 100,
      percentage: 16,
      color: "blue",
      icon: "Factory",
    },
    {
      sector: "Transport",
      emissions: Math.round(totalEmissions * 0.07 * 100) / 100,
      percentage: 7,
      color: "green",
      icon: "Car",
    },
    {
      sector: "Agriculture",
      emissions: Math.round(totalEmissions * 0.04 * 100) / 100,
      percentage: 4,
      color: "emerald",
      icon: "TreePine",
    },
  ];
}

/**
 * Clear the cache (useful for testing or forced refreshes)
 */
export function clearCarbonCache(): void {
  emissionsCache.clear();
  lastCacheTime = 0;
  console.log("🌱 [CARBON SERVICE] Cache cleared");
}
